'use client';

import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';

// 根据实际API数据结构定义Banner接口
interface DivaBannerData {
  banner_image: string;
  banner_text: {
    sparkling: string;
    timeless: string;
    vibe: string;
  };
  coupon_image: string;
  coupon_image_bottom?: string;
  maidian_image?: string; // WAP版本使用
}

interface DivaBannerProps {
  pageData: DivaBannerData; // 直接传入完整的pageData
  deviceType: EPlatform;
}

export default function DivaBanner({ pageData, deviceType }: DivaBannerProps) {
  const setQuickCouponDialog = useStore((state) => state.setQuickCouponDialog);
  const tImages = useTranslations('activity.divaEyeglasses.images');

  const isWap = deviceType !== EPlatform.WEB;

  // 处理第一张优惠券点击
  const handleCouponClick = () => {
    if (setQuickCouponDialog) {
      setQuickCouponDialog({
        show: true,
        eventType: '',
        loginSituation: '',
        triggerType: 'manual',
      });
    }
  };

  if (isWap) {
    // 移动端Banner - 根据WAP版本TopBanner.vue
    return (
      <div className="diva-top-banner w-[375px] max-w-full mx-auto bg-black">
        {/* 主Banner图片 */}
        <Image
          className="banner-image w-full block"
          src={pageData.banner_image}
          alt={tImages('bannerAlt')}
          width={375}
          height={500}
          priority
        />

        {/* 优惠券图片 */}
        <Image
          className="coupon-image w-full block cursor-pointer"
          src={pageData.coupon_image}
          alt={tImages('couponAlt')}
          width={375}
          height={200}
          onClick={handleCouponClick}
        />

        {/* Look at me 区域 */}
        <div className="look-at-me relative w-full">
          <Image
            className="look-at-me-image w-full block"
            src={pageData.maidian_image || pageData.banner_image}
            alt="Diva Glasses & Diva Eyeglass Frames for Party Queen banner2"
            width={375}
            height={600}
          />

          {/* 文本叠加 - 使用绝对定位，与Vue版本位置一致 */}
          <p
            className="sparkling absolute left-[18px] top-[163px] w-[165px] font-br text-[12px] text-[#ffd093] leading-[15px] break-words overflow-hidden text-ellipsis line-clamp-4"
            dangerouslySetInnerHTML={{ __html: pageData.banner_text?.sparkling || '' }}
          />
          <p
            className="aviator absolute left-[18px] top-[268px] w-[165px] font-br text-[12px] text-[#ffd093] leading-[15px] break-words overflow-hidden text-ellipsis line-clamp-4"
            dangerouslySetInnerHTML={{ __html: pageData.banner_text?.timeless || '' }}
          />
          <p
            className="vibe absolute left-[18px] top-[378px] w-[165px] font-br text-[12px] text-[#ffd093] leading-[15px] break-words overflow-hidden text-ellipsis line-clamp-4"
            dangerouslySetInnerHTML={{ __html: pageData.banner_text?.vibe || '' }}
          />
        </div>
      </div>
    );
  }

  // PC端Banner - 根据PC版本Banner.vue + DivaGlasses.vue中的优惠券
  return (
    <div className="diva-banner-pc bg-black">
      {/* 主Banner图片 - 来自Banner.vue */}
      <div className="diva-banner relative w-full">
        <Image
          className="banner-image w-full block"
          src={pageData.banner_image}
          alt={tImages('bannerAlt')}
          width={1920}
          height={1400}
          priority
        />

        {/* 文本叠加 - 使用绝对定位，转换Vue中的px2vw值 */}
        {/* Vue中 px2vw(1316px) ≈ 68.54vw, px2vw(120px) ≈ 6.25vw, px2vw(462px) ≈ 24.06vw */}
        <p
          className="sparking absolute top-[68.54vw] left-[6.25vw] w-[24.06vw] font-br text-[1.25vw] text-[#dcb883] leading-[1.56vw] break-words overflow-hidden text-ellipsis line-clamp-4"
          dangerouslySetInnerHTML={{ __html: pageData.banner_text?.sparkling || '' }}
        />
        <p
          className="timeless absolute top-[68.54vw] left-[35.42vw] w-[24.06vw] font-br text-[1.25vw] text-[#dcb883] leading-[1.56vw] break-words overflow-hidden text-ellipsis line-clamp-4"
          dangerouslySetInnerHTML={{ __html: pageData.banner_text?.timeless || '' }}
        />
        <p
          className="vibe absolute top-[68.54vw] right-[13.02vw] w-[21.09vw] font-br text-[1.25vw] text-[#dcb883] leading-[1.56vw] break-words overflow-hidden text-ellipsis line-clamp-4"
          dangerouslySetInnerHTML={{ __html: pageData.banner_text?.vibe || '' }}
        />
      </div>

      {/* 优惠券图片 - 来自DivaGlasses.vue，位于Banner组件后面 */}
      {pageData.coupon_image && (
        <Image
          className="coupon-image w-full block cursor-pointer"
          src={pageData.coupon_image}
          alt={tImages('couponAlt')}
          width={1920}
          height={400}
          onClick={handleCouponClick}
        />
      )}

      {/* PC版本底部优惠券图片 - 来自DivaGlasses.vue */}
      {pageData.coupon_image_bottom && (
        <Image
          className="coupon-image-bottom w-full block cursor-pointer"
          src={pageData.coupon_image_bottom}
          alt="Diva Glasses & Diva Eyeglass Frames for Party Queen banner2"
          width={1920}
          height={400}
          onClick={handleCouponClick}
        />
      )}
    </div>
  );
}
